import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { computed, ref, inject, watch, PropType } from "vue";
import modalBase from "@/components/ui/modals/modal-base.vue";
import { IDDescriptionPair } from "@/types/generic/IDDescriptionPair";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import displayErrorMessage from "@/utils/ErrorHandling";
import { LookupTableSource } from "acquaint-api/constants/LookupTableSource";
import GridBase from "../ui/grid/grid-base.vue";
import { DxColumn } from "devextreme-vue/data-grid";
import { DxToolbarItem } from "devextreme-vue/popup";
import { displayQuestionAlert } from "@/alerts/questionAlert";
import { useUserPermissionsStore } from "@/stores/UserPermissionsStore";
import { UserPermissions } from "@/types/user/permissions/UserPermissions";
import NewEditMarketingEmailItemsModal from "./new-edit-marketing-email-items-modal.vue";
import { MarketingEmailItemType } from "@/enums/emails/MarketingEmailItemType";
import { AcquaintApiNextClient } from "@/services/AcquaintApiNextClient";
import { DxContextMenu } from "devextreme-vue/context-menu";
import { ContextMenuItem } from "@/types/ui/menus/ContextMenuItem";


export default /*#__PURE__*/_defineComponent({
  __name: 'marketing-email-items-configuration-modal',
  props: {
  /**
   * The boolean value used with `v-model` to control the visibility or state of a modal or component.
   * @type {boolean}
   * @required
   */
  modelValue: {
    type: Boolean,
    required: true,
  },
  /**
   * The type of marketing email item.
   * @type {MarketingEmailItemType}
   * @required
   * @default undefined
   */
  itemType: {
    type: Number as PropType<MarketingEmailItemType>,
    default: undefined,
    require: false,
  },
  /**
   * Determines whether the "Select" button should be displayed.
   * @type {boolean}
   * @default false
   */
  showSelectButton: {
    type: Boolean,
    default: false,
  },
},
  emits: [
  /**
   * Triggered when the `modelValue` changes. This is used to update the v-model variable in the parent component.
   * @property {boolean} value - Indicates whether the modal has been opened or closed.
   */
  "update:modelValue",
  /**
   * Emitted when a template is selected.
   * @property {number} selectedConfigurationItem.id - The id of the selected template.
   * @property {string} selectedConfigurationItem.description - The id of the selected template
   */
  "configurationSelected",
],
  setup(__props, { emit }) {

const props = __props;




const showModal = computed({
  get: () => props.modelValue,
  set: (value: Boolean) => emit("update:modelValue", value),
});

const userPermissionsStore = useUserPermissionsStore();
const acquaintApiClient = inject("AcquaintApiClient") as AcquaintApiClient;
const acquaintApiNextClient = inject(
  "AcquaintApiNextClient",
) as AcquaintApiNextClient;
const marketingEmailItemsList = ref<IDDescriptionPair[]>([]);
const userPermissions = ref<UserPermissions>();
const editNewModalId = ref(0);
const selectedConfigurationItem = ref<IDDescriptionPair>({
  id: 0,
  description: "",
});
const displayEditNewModal = ref(false);
const showFunctionsMenu = ref(false);
const contextMenuId = ref("marketing-email-configuration-action-menu");

const disableEdit = computed(() => {
  return selectedConfigurationItem.value.id == 0;
});

const fetchConfigurationList = async () => {
  try {
    if (props.itemType == undefined) {
      marketingEmailItemsList.value = await acquaintApiClient.httpGet<
        IDDescriptionPair[]
      >(`app/system/lookup/${LookupTableSource.MarketingEmailItems}`);
    } else {
      marketingEmailItemsList.value = await acquaintApiNextClient.httpGet<
        IDDescriptionPair[]
      >(`MarketingEmailItems/${props.itemType}`);
    }
  } catch (err) {
    displayErrorMessage("The selected details could not be fetched");
    marketingEmailItemsList.value = [];
  }
};

watch(
  () => showModal.value,
  async () => {
    if (showModal.value) {
      userPermissions.value = await userPermissionsStore.getUserPermissions();
      fetchConfigurationList();
    }
  },
);

const configurationItemSelected = (e: any) => {
  if (!e) {
    return;
  }
  selectedConfigurationItem.value = e;
};

const configurationItemDoubleClicked = (e: any) => {
  if (!e) {
    return;
  }

  selectedConfigurationItem.value = e;
  editNewModalId.value = selectedConfigurationItem.value.id;
  if (props.showSelectButton) {
    emit(
      "configurationSelected",
      selectedConfigurationItem.value.id,
      selectedConfigurationItem.value.description,
    );
    showModal.value = false;
  } else {
    displayEditNewModal.value = true;
  }
};

const openEditModal = () => {
  if (
    !userPermissions.value ||
    !userPermissions.value.contactsConfiguration.edit
  ) {
    displayErrorMessage(
      "Sorry, you do not have the permission to edit these details.",
    );
    return;
  }

  editNewModalId.value = selectedConfigurationItem.value.id;
  displayEditNewModal.value = true;
};

const openNewModal = () => {
  if (
    !userPermissions.value ||
    !userPermissions.value.contactsConfiguration.new
  ) {
    displayErrorMessage(
      "Sorry, you do not have the permission to create a new system item.",
    );

    return;
  }

  editNewModalId.value = 0;
  displayEditNewModal.value = true;
};

const deleteConfigurationItem = async () => {
  if (
    !userPermissions.value ||
    !userPermissions.value.contactsConfiguration.delete
  ) {
    displayErrorMessage(
      "Sorry, you do not have the permission to delete these details.",
    );
    return;
  }

  if (selectedConfigurationItem.value.id < 1000) {
    displayErrorMessage(
      "Sorry, you can't delete this system item as it is required by Acquaint. However you may edit it, and set it as Inactive to remove it from this list.",
    );
    return;
  }

  const question = await displayQuestionAlert(
    "Are you sure you want to delete these details?",
  );

  if (!question.isConfirmed) {
    return;
  }

  if (await callDeletApi()) {
    selectedConfigurationItem.value = { id: 0, description: "" };
    fetchConfigurationList();
  }
};

const callDeletApi = async () => {
  try {
    await acquaintApiClient.httpDeleteVoid(
      `database/marketingemailitems/${selectedConfigurationItem.value.id}`,
    );
    return true;
  } catch (err) {
    displayErrorMessage(
      "Sorry, these details cannot be deleted as they are referenced elsewhere.",
    );
    return false;
  }
};

const newItemCreated = () => {
  selectedConfigurationItem.value = { id: 0, description: "" };
  fetchConfigurationList();
};

const editItem = (inActive: number) => {
  if (inActive) {
    selectedConfigurationItem.value = { id: 0, description: "" };
  }

  fetchConfigurationList();
};

const menuItems = computed(() => {
  const menu: ContextMenuItem[] = [
    {
      text: "Edit",
      key: "edit",
      disabled: disableEdit.value,
      icon: "",
    },
    {
      text: "Delete",
      key: "delete",
      disabled: disableEdit.value,
      icon: "",
    },
  ];
  return menu;
});
const onMenuItemClick = (e: any) => {
  switch (e.itemData.key) {
    case "edit":
      openEditModal();
      break;
    case "delete":
      deleteConfigurationItem();
      break;
  }
};
const editDeleteButtonsPositioning = computed(() => {
  return props.showSelectButton ? "before" : "after";
});
const selectItem = () => {
  emit(
    "configurationSelected",
    selectedConfigurationItem.value.id,
    selectedConfigurationItem.value.description,
  );
  showModal.value = false;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(modalBase, {
      title: "Marketing Email Items",
      modelValue: showModal.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((showModal).value = $event)),
      "show-close-button": true,
      height: "460px"
    }, {
      "non-scrollable-content": _withCtx(() => [
        _createVNode(GridBase, {
          dataSource: marketingEmailItemsList.value,
          onRowClick: configurationItemDoubleClicked,
          onRowSelected: configurationItemSelected,
          "id-column-name": "id",
          showColumnHeaders: false,
          "pre-selected-id": selectedConfigurationItem.value.id
        }, {
          columns: _withCtx(() => [
            _createVNode(_unref(DxColumn), {
              "data-field": "description",
              caption: "",
              "hiding-priority": 8,
              width: "100%"
            }),
            _createVNode(_unref(DxColumn), {
              "data-field": "id",
              caption: "Id",
              "hiding-priority": 1
            })
          ]),
          _: 1
        }, 8, ["dataSource", "pre-selected-id"])
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Select',
          disabled: disableEdit.value,
          visible: __props.showSelectButton,
          onClick: () => {
            selectItem();
          },
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          elementAttr: { id: contextMenuId.value },
          visible: __props.showSelectButton,
          text: 'Actions',
          onClick: () => {
            showFunctionsMenu.value = !showFunctionsMenu.value;
          },
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: editDeleteButtonsPositioning.value,
          options: {
          text: 'Delete',
          visible: !__props.showSelectButton,
          disabled: disableEdit.value,
          onClick: () => {
            deleteConfigurationItem();
          },
        }
        }, null, 8, ["location", "options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: editDeleteButtonsPositioning.value,
          options: {
          text: 'Edit',
          visible: !__props.showSelectButton,
          disabled: disableEdit.value,
          onClick: () => {
            openEditModal();
          },
        }
        }, null, 8, ["location", "options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'New',
          onClick: () => {
            openNewModal();
          },
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Cancel',
          onClick: () => {
            showModal.value = false;
          },
        }
        }, null, 8, ["options"])
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(NewEditMarketingEmailItemsModal, {
      modelValue: displayEditNewModal.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((displayEditNewModal).value = $event)),
      configurationId: editNewModalId.value,
      onConfigurationItemCreated: newItemCreated,
      onConfigurationItemEdited: editItem
    }, null, 8, ["modelValue", "configurationId"]),
    _createVNode(_unref(DxContextMenu), {
      visible: showFunctionsMenu.value,
      "onUpdate:visible": _cache[2] || (_cache[2] = ($event: any) => ((showFunctionsMenu).value = $event)),
      "data-source": menuItems.value,
      target: `#${contextMenuId.value}`,
      position: { my: 'bottom right', at: 'top right' },
      onItemClick: onMenuItemClick
    }, null, 8, ["visible", "data-source", "target"])
  ], 64))
}
}

})